import { useState, useEffect } from 'react';
import { useOutletContext, Link, useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { Trophy, Users, Repeat, Target, TrendingUp, ChevronRight, Sparkles, Calendar, Store } from 'lucide-react';

interface LayoutContext {
  viewAsStoreId?: string;
  viewAsStoreName?: string;
}

interface WinnerStats {
  totalWinners: number;
  uniqueWinners: number;
  repeatWinners: number;
  avgEntriesPerWinner: number;
  maxEntries: number;
  campaignBreakdown: Array<{
    campaignId: string;
    campaignName: string;
    runId: string;
    winnerCount: number;
  }>;
  topRepeatWinners: Array<{
    customerId: string;
    customerName: string;
    customerEmail: string;
    winCount: number;
  }>;
}

interface StoreVisited {
  id: string;
  name: string;
  scanCount: number;
}

interface WinnerItem {
  id: string;
  customerName: string;
  customerEmail: string;
  customerId: string | null;
  prizeRank: number;
  totalEntries: number;
  selectedAt: string;
  campaignName: string;
  campaignId: string;
  runId: string;
  instanceNumber: number;
  totalWins: number;
  storesVisited: StoreVisited[];
}

interface PendingDraw {
  runId: string;
  campaignId: string;
  campaignName: string;
  instanceNumber: number;
  status: string;
  endDate: string;
  uniqueParticipants: number;
  totalEntries: number;
  numberOfWinners: number;
}

interface WinnersData {
  stats: WinnerStats;
  winners: WinnerItem[];
  pendingDraws: PendingDraw[];
}

export default function WinnersPage() {
  const { viewAsStoreId, viewAsStoreName } = useOutletContext<LayoutContext>() || {};
  const navigate = useNavigate();
  const [data, setData] = useState<WinnersData | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const isStoreView = !!viewAsStoreId;

  useEffect(() => {
    loadData();
  }, [viewAsStoreId]);

  const loadData = async () => {
    setLoading(true);
    setData(null);

    try {
      const storeParam = viewAsStoreId ? `?storeId=${viewAsStoreId}` : '';
      const res = await apiClient.get<{ success: boolean; data: WinnersData }>(`/admin/winners${storeParam}`);
      setData(res.data.data);
    } catch (error) {
      console.error('Failed to load winners data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDrawWinners = (runId: string) => {
    // Navigate to the interactive draw page
    navigate(`/admin/draw/${runId}`);
  };

  const buildLink = (path: string) => {
    if (viewAsStoreId) {
      return `${path}?viewAs=${viewAsStoreId}`;
    }
    return path;
  };

  if (loading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  const filteredWinners = data?.winners.filter(w =>
    w.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.customerEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.campaignName.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-8">
        {isStoreView ? 'My Winners' : 'Winners Dashboard'}
      </h1>

      {/* Pending Draws Section */}
      {!isStoreView && data?.pendingDraws && data.pendingDraws.length > 0 && (
        <div className="mb-8 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-6 h-6 text-amber-600" />
            <h2 className="text-lg font-semibold text-gray-900">Ready to Draw Winners</h2>
          </div>
          <p className="text-gray-600 mb-4">
            These campaigns have ended and are ready for winner selection.
          </p>
          <div className="grid gap-4">
            {data.pendingDraws.map((pd) => (
              <div
                key={pd.runId}
                className="bg-white rounded-lg p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4"
              >
                <div>
                  <h3 className="font-semibold text-gray-900">{pd.campaignName}</h3>
                  <p className="text-sm text-gray-500">Run #{pd.instanceNumber}</p>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {pd.uniqueParticipants} participants
                    </span>
                    <span className="flex items-center gap-1">
                      <Target className="w-4 h-4" />
                      {pd.totalEntries} entries
                    </span>
                    <span className="flex items-center gap-1">
                      <Trophy className="w-4 h-4" />
                      {pd.numberOfWinners} winner{pd.numberOfWinners !== 1 ? 's' : ''} to draw
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleDrawWinners(pd.runId)}
                  className="btn-primary flex items-center gap-2 whitespace-nowrap"
                >
                  <Trophy className="w-4 h-4" />
                  Draw Winners
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Winners"
          value={data?.stats.totalWinners || 0}
          subtitle="All time"
          icon={Trophy}
          bgColor="bg-amber-500"
        />
        <StatCard
          title="Unique Winners"
          value={data?.stats.uniqueWinners || 0}
          subtitle="Different people"
          icon={Users}
          bgColor="bg-indigo-500"
        />
        <StatCard
          title="Repeat Winners"
          value={data?.stats.repeatWinners || 0}
          subtitle="Won multiple times"
          icon={Repeat}
          bgColor="bg-violet-500"
        />
        <StatCard
          title="Avg Entries to Win"
          value={data?.stats.avgEntriesPerWinner || 0}
          subtitle={`Max: ${data?.stats.maxEntries || 0} entries`}
          icon={Target}
          bgColor="bg-sky-500"
        />
      </div>

      {/* Two Column Layout for Extra Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Campaign Breakdown */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Winners by Campaign</h2>
          {data?.stats.campaignBreakdown && data.stats.campaignBreakdown.length > 0 ? (
            <div className="space-y-3">
              {data.stats.campaignBreakdown.map((cb) => (
                <Link
                  key={cb.campaignId}
                  to={buildLink(`/admin/campaigns/${cb.campaignId}/settings`)}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <span className="font-medium text-gray-900">{cb.campaignName}</span>
                  <div className="flex items-center gap-2">
                    <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-sm font-medium">
                      {cb.winnerCount} winner{cb.winnerCount !== 1 ? 's' : ''}
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No winners yet</p>
          )}
        </div>

        {/* Top Repeat Winners */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-violet-500" />
            Top Repeat Winners
          </h2>
          {data?.stats.topRepeatWinners && data.stats.topRepeatWinners.length > 0 ? (
            <div className="space-y-3">
              {data.stats.topRepeatWinners.map((rw, index) => (
                <div
                  key={rw.customerId}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                      index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-700' : 'bg-gray-300'
                    }`}>
                      {index + 1}
                    </span>
                    <div>
                      <p className="font-medium text-gray-900">{rw.customerName}</p>
                      <p className="text-sm text-gray-500">{rw.customerEmail}</p>
                    </div>
                  </div>
                  <span className="bg-violet-100 text-violet-700 px-3 py-1 rounded-full text-sm font-medium">
                    {rw.winCount} wins
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No repeat winners yet</p>
          )}
        </div>
      </div>

      {/* All Winners Table */}
      <div className="card">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h2 className="text-lg font-semibold">All Winners</h2>
          <input
            type="text"
            placeholder="Search winners..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input w-full sm:w-64"
          />
        </div>

        {filteredWinners.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Winner</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Campaign</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Stores Visited</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Prize</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Entries</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Total Wins</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Selected</th>
                </tr>
              </thead>
              <tbody>
                {filteredWinners.map((winner) => (
                  <tr key={winner.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-gray-900">{winner.customerName}</p>
                        <p className="text-sm text-gray-500">{winner.customerEmail}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Link
                        to={buildLink(`/admin/campaigns/${winner.campaignId}/runs/${winner.runId}`)}
                        className="text-cyan-600 hover:text-cyan-700"
                      >
                        {winner.campaignName}
                      </Link>
                      <p className="text-sm text-gray-500">Run #{winner.instanceNumber}</p>
                    </td>
                    <td className="py-3 px-4">
                      {winner.storesVisited && winner.storesVisited.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {winner.storesVisited.map((store) => (
                            <span
                              key={store.id}
                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs"
                              title={`${store.scanCount} scan${store.scanCount > 1 ? 's' : ''}`}
                            >
                              <Store className="w-3 h-3" />
                              {store.name}
                              {store.scanCount > 1 && (
                                <span className="text-gray-500">×{store.scanCount}</span>
                              )}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${
                        winner.prizeRank === 1 ? 'bg-amber-100 text-amber-700' :
                        winner.prizeRank === 2 ? 'bg-gray-100 text-gray-700' :
                        winner.prizeRank === 3 ? 'bg-amber-100 text-amber-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        <Trophy className="w-3 h-3" />
                        {winner.prizeRank === 1 ? '1st' : winner.prizeRank === 2 ? '2nd' : winner.prizeRank === 3 ? '3rd' : `${winner.prizeRank}th`}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-gray-900">{winner.totalEntries}</span>
                    </td>
                    <td className="py-3 px-4">
                      {winner.totalWins > 1 ? (
                        <span className="bg-violet-100 text-violet-700 px-2 py-1 rounded-full text-sm font-medium">
                          {winner.totalWins}x
                        </span>
                      ) : (
                        <span className="text-gray-500">1</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-gray-500 text-sm">
                      {new Date(winner.selectedAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchTerm ? 'No winners match your search' : 'No winners yet'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  bgColor,
}: {
  title: string;
  value: number;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  bgColor: string;
}) {
  return (
    <div className={`${bgColor} rounded-xl p-6 text-white`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-white/80 mb-1">{title}</p>
          <p className="text-3xl font-bold">{value.toLocaleString()}</p>
          <p className="text-sm text-white/70 mt-1">{subtitle}</p>
        </div>
        <Icon className="w-8 h-8 text-white/80" />
      </div>
    </div>
  );
}
